// The job folder's path as people see it in SharePoint / File Explorer, from
// the turnover's saved folder URL (Turnovers.SharePointFolderUrl) — e.g.
//   https://x.sharepoint.com/sites/Construction/Shared%20Documents/CleanSpace/2026%20Jobs/2026-232%20-%20Suite
//   → ["Construction - Documents", "CleanSpace", "2026 Jobs", "2026-232 - Suite"]
// Also accepts a pasted sharing link (/:f:/r/sites/…). Returns [] when the URL
// can't be read as a site folder (the header then shows the link alone). Pure.
import { CONSTRUCTION_SITE_URL, entityBasePath } from "../../utils/sharePoint";
export function folderPathParts(url) {
  let u;
  try { u = new URL(String(url || "")); } catch { return []; }
  let path;
  try { path = decodeURIComponent(u.pathname); } catch { path = u.pathname; }
  path = path.replace(/^\/:[a-z]:\/[a-z]\//i, "/");        // sharing link prefix, e.g. /:f:/r/
  const m = path.match(/^\/(?:sites|teams)\/([^/]+)\/([^/]+)(?:\/(.*))?$/i);
  if (!m) return [];
  const [, site, library, rest = ""] = m;
  const lib = /^(shared documents|documents)$/i.test(library) ? `${site} - Documents` : library;
  return [lib, ...rest.split("/").filter(Boolean)];
}

export const folderPathText = (url) => folderPathParts(url).join(" › ");

// Link to the job folder's Turnover subfolder when the saved URL is a plain
// folder path; a sharing link (/:f:/…) or anything unreadable stays as saved.
export function turnoverFolderUrl(url) {
  const raw = String(url || "");
  if (!folderPathParts(raw).length || /\/:[a-z]:\//i.test(raw)) return raw;
  const [base, query] = raw.split("?");
  return `${base.replace(/\/+$/, "")}/Turnover${query ? `?${query}` : ""}`;
}

// ── Job folder name (2026-09-29) ─────────────────────────────────────────────
// A new job folder is "<job #> - <deal name without its location>". The deal
// name comes from HubSpot, whose naming script is the master logic:
//   Owner - Location - Project [- GC]
// Only its format is relied on here, never its rules: when the name has three
// or more " - " parts and the second looks like a location (no digits, at most
// three words — "SaraSpr NY", "Saratoga Springs", "Edinb"), that part is left
// out. Anything else — a hand-typed name, "Regeneron - Saratoga Building 24
// Project - Consigli" — is used as it is. Existing folders are never renamed;
// they are found by job number (jobFolderForNumber).
const looksLikeLocation = (part) => !/\d/.test(part) && part.split(/\s+/).filter(Boolean).length <= 3;

export function jobFolderDescriptor(projectName) {
  const name = String(projectName || "").trim();
  const parts = name.split(/\s+-\s+/);
  if (parts.length < 3 || !looksLikeLocation(parts[1])) return name;
  return [parts[0], ...parts.slice(2)].join(" - ");
}

export function jobFolderName(jobNumber, projectName) {
  const job = String(jobNumber || "").trim();
  const name = jobFolderDescriptor(projectName);
  return job && name ? `${job} - ${name}` : name;
}

// The one folder in <entity>/<year> Jobs that belongs to this job number —
// "2026-230 - …" or just "2026-230" ("2026-23" never matches "2026-230 …").
// null when there is none, or more than one (then ✏️ Change folder decides).
export function jobFolderForNumber(folders = [], jobNumber = "") {
  const job = String(jobNumber || "").trim().toLowerCase();
  if (!job) return null;
  const hits = (folders || []).filter(f => {
    const n = String(f?.name || "").trim().toLowerCase();
    return n === job || (n.startsWith(job) && /^[\s-]/.test(n.slice(job.length)));
  });
  return hits.length === 1 ? hits[0] : null;
}

// Where this turnover's files are. The saved folder URL when there is one;
// otherwise where a new job folder is created (<prime entity>/<year> Jobs/
// jobFolderName/Turnover). A numbered turnover's existing folder is found by
// job number and saved on open (TurnoverDetail), so this is shown only until
// then. null before a job number.
export function turnoverFolderLocation(turnover) {
  const saved = turnover?.SharePointFolderUrl;
  if (saved) {
    const parts = folderPathParts(saved);
    return { parts: parts.length ? [...parts, "Turnover"] : [], url: turnoverFolderUrl(saved), source: "saved" };
  }
  const job = String(turnover?.JobNumber || "").trim();
  const name = String(turnover?.ProjectName || "").trim();
  if (!job || !name) return null;
  const segs = [entityBasePath(turnover?.ContractingEntity), `${job.split("-")[0]} Jobs`, jobFolderName(job, name), "Turnover"];
  return {
    parts: ["Construction - Documents", ...segs],
    url: `${CONSTRUCTION_SITE_URL}/Shared%20Documents/${segs.map(encodeURIComponent).join("/")}`,
    source: "derived",
  };
}

// Folders offered when changing a turnover's job folder: ones starting with
// the job number first, then the rest, filtered by the search text.
export function rankJobFolders(folders = [], jobNumber = "", query = "") {
  const job = String(jobNumber || "").trim().toLowerCase();
  const q = String(query || "").trim().toLowerCase();
  return (folders || [])
    .filter(f => !q || String(f.name || "").toLowerCase().includes(q))
    .map(f => ({ ...f, matchesJob: !!job && String(f.name || "").toLowerCase().startsWith(job) }))
    .sort((a, b) => (b.matchesJob - a.matchesJob) || String(a.name).localeCompare(String(b.name)));
}

export const setFolderPayload = (turnoverId, folder) =>
  ({ turnover_id: turnoverId, sharepoint_folder_url: folder.webUrl, construction_folder_item_id: folder.id });
